import { useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { ApiError } from '../api/client';
import { authApi } from '../api/endpoints';
import { AuthShell } from './AuthShell';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<{ severity: 'success' | 'error'; message: string } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setStatus(null);
    setSubmitting(true);

    try {
      const { message } = await authApi.forgotPassword(email.trim());
      setStatus({ severity: 'success', message });
      setEmail('');
    } catch (error) {
      setStatus({
        severity: 'error',
        message: error instanceof ApiError ? error.message : 'The request could not be sent.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title="Forgot password"
      subtitle="Enter the email address on your account and we will send a reset link."
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          {status ? <Alert severity={status.severity}>{status.message}</Alert> : null}

          <TextField
            label="Email Address"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            autoFocus
            required
          />

          <Button type="submit" variant="contained" size="large" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send reset link'}
          </Button>

          <Link component={RouterLink} to="/login" variant="body2" align="center">
            Back to sign in
          </Link>
        </Stack>
      </form>
    </AuthShell>
  );
}
